// One emitted point. The procedure does not return one row per billet: it walks
// the billets in order and emits a row only when the R-Factor changes or an hour
// has passed, so the series is a step trace of R-Factor changes.
//
// `start` is when it happened. `startFixed` is where it is drawn: idle time
// between campaigns of the product is removed from the axis, so the two drift
// apart by the total idle time collapsed before that point.
export interface RFactorPoint {
	start: number;
	startFixed: number;
	avg: number | null;
	min: number | null;
	max: number | null;
}

// An idle period removed from the X axis, in real time.
export interface RFactorHollow {
	start: number;
	end: number;
	seconds: number;
}

// A shift boundary inside the plotted range, positioned on the compressed axis.
export interface RFactorShiftChange {
	timestampFixed: number;
	crew: string;
}

export interface RFactorPerStandData {
	points: RFactorPoint[];
	hollows: RFactorHollow[];
	shiftChanges: RFactorShiftChange[];
}

export interface CatalogOption {
	Id: number;
	Name: string;
}

export interface ProductSizeOption extends CatalogOption {
	ProductTypeId: number | null;
}

export interface RFactorPickers {
	crews: string[];
	productSizes: ProductSizeOption[];
	productTypes: CatalogOption[];
}

export interface StandOption {
	Id: number;
	Name: string;
	Order: number;
}

// The legacy pins the axis here and never rescales, so a value that leaves the
// band leaves the chart. Kept: an operator reads this chart by where the line
// sits against a fixed band, not against a range that follows the data.
export const RFACTOR_AXIS_MIN = 0.8;
export const RFACTOR_AXIS_MAX = 1.7;
export const RFACTOR_AXIS_STEP = 0.1;

export const SERIES_COLORS = {
	avg: "#244200",
	limits: "#D800E6",
	shiftChange: "#FA946B",
	hollow: "#d32f2f",
} as const;

export const AXIS_HELP = {
	x: "Time the billet entered the mill. Idle time between campaigns of this product is removed from the axis, so the spacing is rolling time, not clock time.",
	y: "R-Factor at the selected stand: the ratio between the speed the bar leaves this stand at and the speed the next one pulls it at.",
} as const;

// A gap between two campaigns of the same product is routinely weeks long, and
// "1819.0 h" is not a length anybody can picture. Hours up to two days, days
// past that.
export const formatDuration = (seconds: number): string => {
	const hours = seconds / 3600;
	if (hours < 1) return `${Math.round(seconds / 60)} min`;
	if (hours < 48) return `${hours.toFixed(1)} h`;
	return `${(hours / 24).toFixed(1)} d`;
};

// The X axis carries the compressed instants, so a tick has to be translated
// back before it can be labelled. Inside a segment the two differ by a constant,
// which makes the inverse exact rather than interpolated.
export const buildFixedToRealTime = (points: RFactorPoint[]) => {
	const offsets = points.map((p) => ({
		fixed: p.startFixed,
		offset: p.start - p.startFixed,
	}));

	return (fixed: number): number => {
		let offset = offsets.length ? offsets[0].offset : 0;
		for (const entry of offsets) {
			if (entry.fixed > fixed) break;
			offset = entry.offset;
		}
		return fixed + offset;
	};
};
